import React, {
    Key,
    memo,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    CSSProperties,
    forwardRef,
    ForwardedRef,
} from 'react';
import { useTabList } from 'react-aria';
import { useTabListState } from 'react-stately';
import { useLogger } from '@direct-frontend/modules-logger';
import { useResizeObserver, useEventListener } from '@direct-frontend/stdlib';

import { getScrollbarClassName } from '../../lib/css/getScrollbarClassName';
import { useHStack } from '../../hooks/useStack';
import { roundByDPR } from '../../lib/dom/roundByDPR/roundByDPR';
import { getColorSkin } from '../../lib/skin/getColorSkin/getColorSkin';
import { getParentOffsets } from '../../lib/dom/getParentOffsets/getParentOffsets';
import { VStack } from '../Stack';

import { getDefaultTabRenderer, Tab } from './Tab/Tab';
import { TabPanel } from './TabPanel/TabPanel';
import { TabMenuProps } from './types';
import { tabMenuCls, gapMap } from './utils';

import './TabMenu.css';

const scrollClass = getScrollbarClassName();

/**
 * Компонент для создания горизонтального меню
 */
export const TabMenu = memo(
    forwardRef<HTMLDivElement, TabMenuProps>(function TabMenu(
        {
            items,
            selectedKey,
            defaultKey,
            onChange,
            size = 'xs',
            isUnderlined = true,
            width = 'auto',
            contentGap,
            className,
            tabPanelClassName,
            tabsClassName,
            hasTopPadding = true,
            ...props
        },
        ref: ForwardedRef<HTMLDivElement>,
    ) {
        // логирование при клике на вкладку и показе
        const { LogProvider, logClick } = useLogger('TabMenu', props.logNode);

        const [sliderProps, setSliderProps] = useState<CSSProperties | undefined>();
        const [isOverflowing, setIsOverflowing] = useState(false);

        const tabListRef = useRef<HTMLUListElement>(null);
        const activeTabRef = useRef<HTMLAnchorElement>(null);
        const containerRef = useRef<HTMLDivElement>(null);

        const colorSkinClass = getColorSkin('gray-transparent', {});

        const disabledTabs = useMemo(() => {
            return items.reduce<string[]>((result, item) => {
                if (item.disabled) {
                    result.push(item.key);
                }

                return result;
            }, []);
        }, [items]);

        const onSelectionChangeHandler = useCallback(
            (key: Key) => {
                logClick({ type: 'onSelectionChange', value: String(key) });
                onChange?.(String(key));
            },
            [logClick, onChange],
        );

        const updateIfOverflows = useCallback(() => {
            // -1 Нужен чтобы предотвращать некорректную работу с субпикселями
            if (containerRef.current && containerRef.current?.scrollWidth - 1 > containerRef.current?.clientWidth) {
                setIsOverflowing(true);
                return;
            }
            setIsOverflowing(false);
        }, []);

        const updateSliderSizeAndPosition = useCallback(() => {
            if (!activeTabRef.current || !tabListRef.current) {
                return;
            }

            const { width: wrapperWidth } = tabListRef.current.getBoundingClientRect();

            // Высчитываем точный, дробный width
            const { width } = activeTabRef.current.getBoundingClientRect();
            // Высчитываем точный, дробный offsetLeft
            const { left: leftPaddingFromContainer } = getParentOffsets(activeTabRef.current) ?? {};

            if (leftPaddingFromContainer === undefined) {
                return;
            }

            /**
             * Используем в качестве результата проценты, чтобы в случае down/upscale-ов
             * в процессе анимаций, это не ломало реальную поцизию элемента
             *
             * Прецедент: https://st.yandex-team.ru/DIRECT-283740
             */
            setSliderProps({
                // Округляем ширину под пиксельную сетку экрана пользователя
                // Приводим ширину к проценту относительно родителя
                width: `${(roundByDPR(width) / roundByDPR(wrapperWidth)) * 100}%`,
                // Округляем позицию под пиксельную сетку экрана пользователя
                // Приводим отступ к проценту относительно ширины элемента
                transform: `translateX(${(roundByDPR(leftPaddingFromContainer) / roundByDPR(width)) * 100}%)`,
            });
        }, [activeTabRef, tabListRef]);

        const tabListState = useTabListState({
            items: items,
            children: getDefaultTabRenderer(),
            selectedKey: selectedKey,
            defaultSelectedKey: defaultKey,
            disabledKeys: disabledTabs,
            onSelectionChange: onSelectionChangeHandler,
        });

        const { tabListProps } = useTabList(
            {
                orientation: 'horizontal',
                keyboardActivation: 'automatic',
                items: items,
                'aria-label': props['aria-label'],
            },
            tabListState,
            tabListRef,
        );

        const recalculatePositions = useCallback(() => {
            updateIfOverflows();
            updateSliderSizeAndPosition();
        }, [updateIfOverflows, updateSliderSizeAndPosition]);

        const observer = useResizeObserver(recalculatePositions);

        useEffect(() => {
            // отслеживание размера контейнера требуется для предотвращения возможных визуальных багов
            if (containerRef.current) {
                observer?.observe(containerRef.current);
            }

            return () => {
                observer?.disconnect();
            };
        }, [observer]);

        // Подписываемся на изменение выбранной вкладки и/или изменении списка табов
        useEffect(() => {
            recalculatePositions();
        }, [tabListState.selectedKey, selectedKey, items, recalculatePositions]);

        // Обрабатываем кейс, когда из-за смены масштаба меняется финальный размер шрифта
        useEventListener('resize', recalculatePositions);

        const tabsStackClasses = useHStack({
            gap: gapMap[size],
            flexWrap: 'nowrap',
            justifyContent: width === 'max' ? 'center' : 'flex-start',
            alignItems: 'center',
        });

        // подготовка вкладок
        const tabs = [...tabListState.collection].map(item => (
            <Tab
                key={item.key}
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- item.props оставляем тип any
                item={item.props}
                state={tabListState}
                size={size}
                activeTabRef={activeTabRef}
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions, @typescript-eslint/no-unsafe-member-access -- item.props оставляем тип any
                data-testid={props['data-testid'] && `${props['data-testid']}.${item.props.key}`}
            />
        ));

        const slider = sliderProps ? <div className={tabMenuCls('slider', { size })} style={sliderProps} /> : undefined;

        return (
            <LogProvider>
                <VStack
                    gap={contentGap}
                    className={tabMenuCls({ width, hasTopPadding }, [colorSkinClass, className])}
                    data-testid={props['data-testid']}
                    ref={ref}
                >
                    <div
                        className={tabMenuCls('container', { withUnderline: isUnderlined, scrollable: isOverflowing }, [
                            tabsClassName,
                            isOverflowing ? scrollClass : undefined,
                        ])}
                        ref={containerRef}
                    >
                        <ul
                            {...tabListProps}
                            ref={tabListRef}
                            className={tabMenuCls('tabs', [colorSkinClass, tabsStackClasses])}
                        >
                            {tabs}
                            {slider}
                        </ul>
                    </div>
                    <TabPanel key={tabListState.selectedItem?.key} state={tabListState} className={tabPanelClassName} />
                </VStack>
            </LogProvider>
        );
    }),
);
